import { Redirect, useRouteMatch } from 'react-router-dom';
import { useCurrentCommunityId, useMyMembers } from '../../redux/selector';
import React, { useEffect } from 'react';
import { COMMUNITY_PREFIX_PATH, COMMUNITY_KEY } from '../../configs/AppConfig';
import { connect } from 'react-redux';
import { setCurrentCommunityId, clearCurrentCommunityId } from '../../redux/actions';

interface LoadCurrentCommunityProps {
  setCurrentCommunityId: (communityId: number) => void;
  clearCurrentCommunityId: () => void;
}

interface RouteParam {
  communityId: string;
}

const LoadCurrentCommunity: React.FC<React.PropsWithChildren<LoadCurrentCommunityProps>> = ({
  setCurrentCommunityId,
  clearCurrentCommunityId,
  children,
}) => {
  const communityIdFromRoute = useCommunityIdFromRoute();
  const accessibleCommunityIds = useMyMembers().map((it) => it.communityId);
  const desiredCommunityId = determineCurrentCommunityId(communityIdFromRoute, accessibleCommunityIds);
  useEffect(() => {
    if (desiredCommunityId) {
      setCurrentCommunityId(desiredCommunityId);
      saveCommunityIdToCache(desiredCommunityId);
    } else {
      clearCurrentCommunityId();
    }
  }, [desiredCommunityId, setCurrentCommunityId, clearCurrentCommunityId]);
  const communityIdFromRedux = useCurrentCommunityId();
  if (communityIdFromRoute !== desiredCommunityId) {
    return <Redirect to={`${COMMUNITY_PREFIX_PATH}/${desiredCommunityId}`} />;
  }
  if (communityIdFromRoute !== communityIdFromRedux) {
    // Wait until the redux state is in-sync with the route.
    return <></>;
  }
  return <>{children}</>;
};

const determineCurrentCommunityId = (
  communityIdFromUrl: number | undefined,
  accessibleCommunityIds: number[]
): number | undefined => {
  if (communityIdFromUrl && accessibleCommunityIds.includes(communityIdFromUrl)) {
    return communityIdFromUrl;
  }
  const communityIdFromCache = loadCommunityIdFromCache();
  if (communityIdFromCache && accessibleCommunityIds.includes(communityIdFromCache)) {
    return communityIdFromCache;
  }
  if (accessibleCommunityIds.length > 0) {
    return accessibleCommunityIds[0];
  }
  return undefined;
};

const useCommunityIdFromRoute = (): number | undefined => {
  const routeMatch = useRouteMatch<RouteParam>();
  const communityIdParam = routeMatch.params.communityId;
  const communityId = parseInt(communityIdParam);
  if (!communityId) {
    // Return undefined if NaN.
    return undefined;
  }
  return communityId;
};

const loadCommunityIdFromCache = () => {
  const savedCommunityId = localStorage.getItem(COMMUNITY_KEY);
  if (!savedCommunityId) {
    return undefined;
  }
  const communityId = parseInt(savedCommunityId);
  if (!communityId) {
    // Return undefined if NaN.
    return undefined;
  }
  return communityId;
};

const saveCommunityIdToCache = (communityId: number) => {
  localStorage.setItem(COMMUNITY_KEY, communityId.toString());
};

export default connect(null, { setCurrentCommunityId, clearCurrentCommunityId })(LoadCurrentCommunity);
